import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EventCard } from "@/lib/producerTypes";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarDays, MapPin } from "lucide-react";

interface ProducerEventCardProps {
  event: EventCard;
}

export const ProducerEventCard = ({ event }: ProducerEventCardProps) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/events/${event.slug}`);
  };

  const formatEventDate = (startAt: string, timezone: string = 'UTC') => {
    try {
      return formatInTimeZone(new Date(startAt), timezone, 'EEE, d MMM, h:mmaaa zzz');
    } catch {
      return formatInTimeZone(new Date(startAt), 'UTC', 'EEE, d MMM, h:mmaaa');
    }
  };

  const getLocationString = () => {
    const parts = [];
    if (event.venueName) parts.push(event.venueName);
    if (event.city) parts.push(event.city);
    if (event.region) parts.push(event.region);
    if (event.country) parts.push(event.country);
    return parts.join(', ');
  };

  return (
    <Card 
      className="group cursor-pointer overflow-hidden bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
      onClick={handleCardClick}
    >
      {/* Image section */}
      <div className="relative aspect-video overflow-hidden">
        {event.imageUrl ? (
          <img
            src={event.imageUrl}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-500/50 to-pink-500/50 flex items-center justify-center">
            <div className="text-white/80 text-center p-4">
              <CalendarDays className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm font-medium">{event.title}</p>
            </div>
          </div>
        )}
        
        {/* Status badge */}
        {event.status && event.status !== "scheduled" && event.status !== "published" && (
          <Badge 
            variant={event.status === "canceled" ? "destructive" : "secondary"}
            className="absolute top-3 right-3 capitalize"
          >
            {event.status}
          </Badge>
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <CardContent className="p-6 space-y-4">
        {/* Title */}
        <h3 className="text-lg font-bold text-white line-clamp-2 group-hover:text-purple-300 transition-colors duration-300">
          {event.title}
        </h3>

        {/* Date */}
        <div className="flex items-center gap-2 text-white/70">
          <CalendarDays className="w-4 h-4 text-purple-400" />
          <p className="text-sm">
            {formatEventDate(event.startAt, event.timezone)}
          </p>
        </div>

        {/* Location */}
        {getLocationString() && (
          <div className="flex items-center gap-2 text-white/70">
            <MapPin className="w-4 h-4 text-pink-400" />
            <p className="text-sm line-clamp-1">
              {getLocationString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};